import React, { useRef } from 'react';
import { motion } from 'framer-motion';
import { StarIcon, ArrowRightIcon, GiftIcon, UserGroupIcon } from '@heroicons/react/24/outline';
import { useLanguage } from '../../contexts/LanguageContext';
import { mapDualProgramLinks } from '../../utils/referralLinks';
import RewardCardVisual from '../shared/RewardCardVisual';
import ClientQuickInviteCard from './ClientQuickInviteCard';
import InviteContactsMiniCard from './InviteContactsMiniCard';
import NetworkMemberCard from './NetworkMemberCard';
import ShortActionsPanel from './ShortActionsPanel';
import ActionOutcomePanel from '../ui/ActionOutcomePanel';
import Button from '../ui/Button';
import { StaggerContainer, StaggerItem } from '../ui/Stagger';
import EmptyState from '../ui/EmptyState';
import { CardGridSkeleton } from '../ui/PageSkeleton';

function RewardShopCard({ reward, claimingRewardId, onClaim, t, formatMessage }) {
  const isClaiming = claimingRewardId === reward.id;

  return (
    <motion.article
      layout
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      className={`group flex h-full flex-col overflow-hidden rounded-2xl border bg-white shadow-soft transition-[box-shadow,transform] duration-300 hover:-translate-y-0.5 hover:shadow-medium  ${
        reward.has_active_claim
          ? 'border-secondary-200 '
          : reward.can_redeem
            ? 'border-success-300/80 ring-1 ring-success-200/60 '
            : 'border-secondary-200 '
      }`}
    >
      <div className="relative aspect-[4/3] overflow-hidden bg-secondary-100 ">
        <RewardCardVisual reward={reward} />
        <div className="absolute left-3 top-3 inline-flex items-center gap-1 rounded-full bg-black/55 px-2.5 py-1 text-xs font-semibold text-white backdrop-blur-sm">
          <StarIcon className="h-3.5 w-3.5 text-amber-400" />
          {reward.points_required} pts
        </div>
        {reward.can_redeem && !reward.has_active_claim ? (
          <span className="absolute right-3 top-3 rounded-full bg-success-500 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white">
            {t.dashboard.available}
          </span>
        ) : null}
      </div>

      <div className="flex flex-1 flex-col p-4">
        <h3 className="font-semibold text-secondary-900 ">{reward.name}</h3>
        <p className="mt-1 line-clamp-2 flex-1 text-sm text-secondary-600 ">
          {reward.description || t.dashboard.noDescription}
        </p>

        {reward.has_active_claim ? (
          <button
            type="button"
            disabled
            className="mt-4 w-full cursor-not-allowed rounded-xl bg-secondary-200 py-2.5 text-sm font-semibold text-secondary-500  "
          >
            {t.dashboard.giftOnTheWay}
          </button>
        ) : reward.can_redeem ? (
          <Button
            type="button"
            variant="primary"
            size="small"
            className="mt-4 w-full"
            disabled={isClaiming}
            loading={isClaiming}
            onClick={() => onClaim(reward.id)}
          >
            {isClaiming ? t.dashboard.creating : t.dashboard.claimReward}
          </Button>
        ) : (
          <p className="mt-4 text-center text-xs font-medium text-secondary-500 ">
            {formatMessage(t.dashboard.moreNeeded, { count: reward.points_needed })}
          </p>
        )}
      </div>
    </motion.article>
  );
}

function getReferralStateLabel(state, t) {
  const pipeline = t.enrola.network?.pipeline || {};
  const map = {
    affiliated: pipeline.registered,
    contacted: pipeline.verified,
    visited: pipeline.verified,
    completed: pipeline.rewardPaid,
  };
  return map[state] || pipeline.registered || state;
}

function ClientHomeView({
  availableRewards = [],
  rewardsLoading,
  referralLinks = [],
  networkMembers = [],
  networkLoading = false,
  claimingRewardId,
  onClaimReward,
  onOpenRewards,
  onOpenReferrals,
  actionOutcome,
  onDismissActionOutcome,
  shortActions = [],
  shortActionsLoading = false,
  onRefreshShortActions,
  onShortActionPointsEarned,
  hideRewardsShop = false,
}) {
  const { t, formatMessage } = useLanguage();
  const h = t.enrola.clientHome;
  const { clientsLink, partnersLink } = mapDualProgramLinks(referralLinks);
  const displayRewards = hideRewardsShop ? [] : availableRewards.slice(0, 6);
  const displayReferrals = networkMembers.slice(0, 10);

  const shortActionRunnerRef = useRef(null);

  const inviteLabels = {
    inviteEyebrow: h.inviteEyebrow,
    inviteTitle: h.inviteTitle,
    inviteSubtitle: h.inviteSubtitle,
    scanToJoin: h.scanToJoin,
    openReferrals: h.openReferrals,
    noInviteLink: h.noInviteLink,
  };

  const partnerLabels = t.enrola.partnerHome;

  return (
    <div className="content-flow">
      <ActionOutcomePanel
        outcome={actionOutcome}
        onDismiss={onDismissActionOutcome}
        className="mb-2"
      />

      <ShortActionsPanel
        actions={shortActions}
        loading={shortActionsLoading}
        role="client"
        layout="banner"
        onRefresh={onRefreshShortActions}
        onPointsEarned={onShortActionPointsEarned}
        onNavigateRewards={onOpenRewards}
        actionRunnerRef={shortActionRunnerRef}
      />

      <div className={`grid gap-5 xl:gap-6 ${hideRewardsShop ? '' : 'lg:grid-cols-2 lg:items-stretch'}`}>
        <section className="min-w-0">
          <ClientQuickInviteCard
            link={clientsLink}
            secondaryLink={partnersLink}
            onOpenReferrals={onOpenReferrals}
            labels={inviteLabels}
            secondaryLabels={partnerLabels}
            layout="vertical"
            compact
            qrSize={240}
            className="h-full"
          />
        </section>

        {!hideRewardsShop ? (
        <section className="min-w-0 space-y-5">
          <div className="flex flex-wrap items-end justify-between gap-3">
            <div>
              <p className="micro text-orange-600 ">
                {h.shopEyebrow}
              </p>
              <h3 className="text-xl font-bold text-secondary-900  sm:text-2xl">
                {h.shopTitle}
              </h3>
              <p className="mt-1 text-sm text-secondary-600 ">{h.shopSubtitle}</p>
            </div>
            {onOpenRewards ? (
              <button
                type="button"
                onClick={onOpenRewards}
                className="text-sm font-semibold text-orange-600 hover:text-orange-700 "
              >
                {h.viewCatalog}
              </button>
            ) : null}
          </div>

          {rewardsLoading ? (
            <CardGridSkeleton cards={2} className="sm:grid-cols-2" />
          ) : displayRewards.length > 0 ? (
            <StaggerContainer className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              {displayRewards.map((reward, index) => (
                <StaggerItem key={reward.id} className={index >= 2 ? 'lg:hidden' : ''}>
                  <RewardShopCard
                    reward={reward}
                    claimingRewardId={claimingRewardId}
                    onClaim={onClaimReward}
                    t={t}
                    formatMessage={formatMessage}
                  />
                </StaggerItem>
              ))}
            </StaggerContainer>
          ) : (
            <EmptyState
              icon={GiftIcon}
              title={t.dashboard.noRewardsAvailable}
              description={t.dashboard.checkBackLater}
            />
          )}
        </section>
        ) : null}
      </div>

      <section className="min-w-0 border-t border-secondary-100 pt-5 ">
            <div className="mb-3 flex flex-wrap items-end justify-between gap-2">
              <div>
                <p className="micro text-secondary-500 ">
                  {h.referralsEyebrow}
                </p>
                <h4 className="text-base font-bold text-secondary-900  sm:text-lg">
                  {h.referralsTitle}
                </h4>
              </div>
              {onOpenReferrals && displayReferrals.length > 0 ? (
                <button
                  type="button"
                  onClick={onOpenReferrals}
                  className="inline-flex items-center gap-1 text-sm font-semibold text-orange-600 hover:text-orange-700 "
                >
                  {h.viewAllReferrals}
                  <ArrowRightIcon className="h-4 w-4" />
                </button>
              ) : null}
            </div>

            {networkLoading ? (
              <div className="flex gap-3 overflow-x-auto pb-1">
                {[1, 2, 3, 4].map((i) => (
                  <div
                    key={i}
                    className="h-[88px] min-w-[168px] shrink-0 animate-pulse rounded-2xl bg-secondary-100 "
                  />
                ))}
              </div>
            ) : displayReferrals.length > 0 ? (
              <div className="flex snap-x snap-mandatory gap-3 overflow-x-auto scroll-smooth pb-1 [-ms-overflow-style:none] [scrollbar-width:thin]">
                {displayReferrals.map((member) => (
                  <NetworkMemberCard
                    key={member.id}
                    name={member.name}
                    avatarUrl={member.avatar_url}
                    joinedLabel={
                      member.joined
                        ? new Date(member.joined).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
                        : ''
                    }
                    stateLabel={getReferralStateLabel(member.state, t)}
                    referralsLabel={formatMessage(t.enrola.referralsCount, { count: member.referrals || 0 })}
                  />
                ))}
              </div>
            ) : (
              <div className="rounded-2xl border border-dashed border-secondary-200 bg-secondary-50/50 px-4 py-5 text-center  ">
                <UserGroupIcon className="mx-auto mb-2 h-6 w-6 text-secondary-400" />
                <p className="text-sm text-secondary-600 ">{h.noReferralsYet}</p>
                {onOpenReferrals ? (
                  <button
                    type="button"
                    onClick={onOpenReferrals}
                    className="mt-2 text-sm font-semibold text-orange-600 hover:text-orange-700 "
                  >
                    {h.inviteFirstReferral}
                  </button>
                ) : null}
              </div>
            )}
      </section>

      <InviteContactsMiniCard link={clientsLink} />
    </div>
  );
}

export default ClientHomeView;
